// Tema kayit defteri: token normalizasyonu, tema→asset ve tema→grafik paleti
// haritalari. Yeni tema eklerken: KNOWN_THEMES + iki haritaya kayit +
// cg-themes.scss'e :root[data-cg-theme='<token>'] blogu yeterlidir.

import { environment } from '../../../environments/environment';

export type CgThemeToken = 'carbongate' | 'turkcell';

export const CG_DEFAULT_THEME: CgThemeToken = 'carbongate';

/** theme-init.js ile paylasilan localStorage anahtari (login ekrani icin son bilinen tema). */
export const CG_THEME_STORAGE_KEY = 'cg_theme';

const KNOWN_THEMES: CgThemeToken[] = ['carbongate', 'turkcell'];

/** API'den gelen tema adini token'a cevirir; bilinmeyen/bos deger varsayilana duser. */
export function normalizeThemeName(name: string | null | undefined): CgThemeToken {
  const token = (name || '').trim().toLowerCase() as CgThemeToken;
  return KNOWN_THEMES.includes(token) ? token : CG_DEFAULT_THEME;
}

/**
 * On-prem kurulumlar (TCOnPremLocal/Test/Preprod/Prod) `environment.ts`
 * icinde `forcedTheme: 'turkcell'` tanimlar; bu 4 build icin tema derleme
 * zamaninda sabittir ve API'nin currentUser.themeName degeri TAMAMEN
 * yoksayilir (ThemeService.init bkz.). Diger environmentlarda bu alan
 * tanimli degildir -> null, tema normal akisla API'den gelir.
 *
 * Neden environment.ts uzerinden: proje zaten `fileReplacements` (angular.json)
 * ile her build hedefi icin ayri bir environment dosyasi kullaniyor; ayni
 * mekanizmayi tema icin de kullanmak yeni bir konfigurasyon katmani
 * eklemeden (env degiskeni, ayri build script'i vb.) tek dogruluk kaynagini
 * korur.
 */
export const CG_FORCED_THEME: CgThemeToken | null = (() => {
  const raw = (environment as { forcedTheme?: string }).forcedTheme;
  if (!raw) {
    return null;
  }
  const token = raw.trim().toLowerCase() as CgThemeToken;
  return KNOWN_THEMES.includes(token) ? token : null;
})();

export interface CgThemeAssets {
  /** Login + sifre ekranlari genis logo */
  logoWide: string;
  /** Sidebar alt genis logo */
  logoSidebarWide: string;
  /** Sidebar alt dar (collapsed) logo */
  logoSidebarCompact: string;
  /** Company-select / splash isareti */
  logoMark: string;
  /** SSO ekranlari logosu */
  logoPlain: string;
  /** Tarayici sekmesi favicon'u (bkz. index.html #cg-favicon + theme-init.js) */
  favicon: string;
  /** favicon dosyasinin MIME type'i (link[type] icin) */
  faviconType: string;
}

const CARBONGATE_ASSETS: CgThemeAssets = {
  logoWide: './assets/media/logos/carbongate_corporate-48.png',
  logoSidebarWide: './assets/media/logos/carbongate_corporate-47.png',
  logoSidebarCompact: './assets/media/logos/cgate-favicons/carbongate_corporate-41.svg',
  logoMark: './assets/media/logos/cgate-favicons/carbongate_corporate-49.png',
  logoPlain: './assets/media/logos/carbon-gate-logo.png',
  favicon: './assets/media/logos/cgate-favicons/favicon.ico',
  faviconType: 'image/x-icon',
};

// Turkcell: marka rehberi (SUNUM_FORMATI_200125) ana logosundan uretildi.
// -white varyanti koyu lacivert zeminler (login/sidebar), mark her iki zeminde.
const TURKCELL_ASSETS: CgThemeAssets = {
  logoWide: './assets/media/logos/turkcell/turkcell-logo-white.png',
  logoSidebarWide: './assets/media/logos/turkcell/turkcell-logo-white.png',
  logoSidebarCompact: './assets/media/logos/turkcell/turkcell-mark.png',
  logoMark: './assets/media/logos/turkcell/turkcell-mark.png',
  logoPlain: './assets/media/logos/turkcell/turkcell-logo.png',
  favicon: './assets/media/logos/turkcell/turkcell-mark.png',
  faviconType: 'image/png',
};

export const CG_THEME_ASSETS: Record<CgThemeToken, CgThemeAssets> = {
  carbongate: CARBONGATE_ASSETS,
  turkcell: TURKCELL_ASSETS,
};

export interface CgChartPalette {
  scope1: string;
  scope2: string;
  scope3: string;
  purple: string;
  red: string;
  ink: string;
  ink2: string;
  ink3: string;
  line: string;
}

export const CG_CHART_PALETTES: Record<CgThemeToken, CgChartPalette> = {
  // dashboard-cg-palette.ts'ten tasindi — styles.scss .cg-screen remap'i ile ayni aile.
  carbongate: {
    scope1: '#16805b', // cg green-600  (eski --bs-success #17C653)
    scope2: '#185fa5', // cg blue-600   (eski --bs-primary #1B84FF)
    scope3: '#b57e2a', // cg amber-500  (bar dolgusu; metin icin amber-700 #854f0b)
    purple: '#534ab7', // ikincil seri  (cg info)
    red: '#c03a3a', //    cg red-600
    ink: '#1e2723', //    dataLabels    (eski --bs-gray-800)
    ink2: '#5c6963', //   kategori ekseni + legend (eski '#222b45' / --bs-gray-500)
    ink3: '#93a09a', //   sayisal eksen etiketleri (eski '#888')
    line: '#e4e9e6', //   grid cizgisi  (eski '#e5e7eb' / --bs-gray-200)
  },
  // Turkcell Aralik 2025 marka rehberi paleti — B.4.1 Lumos (Yardimci
  // Kurumsal Renkler) ile birebir: #1d5aff, #001484, #ffca00, #00c2ff.
  //
  // scope1/scope2/scope3 BILEREK carbongate ile ayni (yesil/mavi/amber):
  // Kapsam 1/2/3 kart+grafik renkleri kullanicilar icin sabit bir okuma
  // kurali (yesil=Kapsam1, mavi=Kapsam2, sari-amber=Kapsam3) — temaya gore
  // degismemeli, marka rengine gore degil veri anlamina gore renklenir.
  turkcell: {
    scope1: '#16805b', // cg green-600 — carbongate ile ayni, temayla degismez
    scope2: '#185fa5', // cg blue-600  — carbongate ile ayni, temayla degismez
    scope3: '#b57e2a', // cg amber-500 — carbongate ile ayni, temayla degismez
    purple: '#00c2ff', // acik mavi (info)
    red: '#c03a3a',
    ink: '#0b1030',
    ink2: '#5a5f73',
    ink3: '#9a9eb1',
    line: '#e3e4ea',
  },
};

export function getCgChart(theme: CgThemeToken): CgChartPalette {
  return CG_CHART_PALETTES[theme] || CG_CHART_PALETTES[CG_DEFAULT_THEME];
}
